import { useEffect, useMemo, useState } from 'react';
import { ProductBundle, ProductWithBundleId } from '~/models/CampaignModel';
import { SelectionMode } from '~/models/SelectionModel';
import { getVisibleBestDiscountOffers } from '~/store/campaign/campaign.utils';
import { useBasket } from './useBasket';

export type MappedGroup = ReturnType<typeof useMappedBundles>[number];
export const useMappedBundles = (bundles: ProductBundle<ProductWithBundleId>[]) => {
  return bundles.map((bundle) => useMappedBundle(bundle));
};

const useMappedBundle = (bundle: ProductBundle<ProductWithBundleId>) => {
  const selectedProducts = useBasket((s) => s.selectedProducts);
  const [selectedInBundle, setSelectedInBundle] = useState(() => selectedProducts.filter((o) => o.bundleId === bundle.id));

  // only update selectedInBundle value when there is an actual change in the bundle
  // this is to prevent flickering in the UI when selecting products
  useEffect(() => {
    setSelectedInBundle((lastState) => {
      const newSelection = selectedProducts.filter((o) => o.bundleId === bundle.id);
      if (newSelection.length !== lastState.length) {
        return newSelection;
      }
      if (
        newSelection.every((p) => lastState.some((l) => l.id === p.id)) &&
        lastState.every((p) => newSelection.some((l) => l.id === p.id))
      ) {
        return lastState;
      }
      return newSelection;
    });
  }, [selectedProducts, bundle.id]);

  const visibleItems = useMemo(() => {
    return bundle.selectionConfig.selectionMode !== SelectionMode.BestDiscount
      ? bundle.offers
      : getVisibleBestDiscountOffers<ProductWithBundleId>(selectedInBundle, bundle);
  }, [selectedInBundle, bundle]);

  return {
    bundle,
    selectedInBundle,
    visibleItems,
  };
};
